import { useQuery } from '@tanstack/react-query'

import { BACKUP, BACKUP_ERRORS } from '@brickup/shared/locales/de/backup'
import type { BackupOverview } from '@brickup/shared/models'
import { isJobLive } from '@brickup/shared/models/job'
import { apiClient } from './client'
import type { GlanceOptions } from './glance'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

export type { BackupOverview }

const BASE = '/admin/backup'

export const BACKUP_OVERVIEW_KEY = ['backup-overview'] as const

async function fetchOverview(): Promise<BackupOverview> {
  const res = await apiClient.get<BackupOverview>(BASE)
  return res.data
}

/**
 * The archive lying in the bucket and the run in flight, in one read.
 *
 * Polled quickly while something is running and slowly otherwise: the screen
 * is the only place a run reports itself, and an operator who left the tab
 * open wants the download to appear on its own. The state lives on the server,
 * so a reload picks the run back up rather than losing it.
 *
 * `refetchInterval` is the idle cadence. A read costs a bucket listing and a
 * job scan, so a caller that only glances at the archive raises it; the live
 * cadence is fixed.
 */
export function useBackupOverview({
  refetchInterval = 30_000,
  ...options
}: GlanceOptions = {}) {
  return useQuery({
    queryKey: BACKUP_OVERVIEW_KEY,
    queryFn: fetchOverview,
    refetchInterval: query =>
      isJobLive(query.state.data?.running) ? 2_000 : refetchInterval,
    ...options,
  })
}

/** Throws the archive away now rather than waiting for the lifecycle rule. */
export function useDeleteArchive() {
  return useMutationWithInvalidation<void>(
    async () => {
      await apiClient.delete(BASE)
    },
    [BACKUP_OVERVIEW_KEY],
    {
      // Settled, not success: a delete that failed after S3 removed the object
      // would otherwise leave the screen offering an archive that is gone.
      invalidateOn: 'settled',
      errorFallback: BACKUP.archive.deleteFailed,
    },
  )
}

export function useStartBackup() {
  return useMutationWithInvalidation<{ jobId: string }>(
    async () => (await apiClient.post<{ jobId: string }>(`${BASE}/start`)).data,
    [BACKUP_OVERVIEW_KEY],
    { errorFallback: BACKUP_ERRORS.startFailed },
  )
}
